/**
 * CDP SDK — remote-managed Site Profile loader.
 *
 * Fetches the per-workspace Site Profile from {apiHost}/v1/config?key=..., caches
 * it in localStorage (stale-while-revalidate), and resolves the effective
 * profile as: built-in defaults ← cached/remote ← inline override.
 *
 * Fail-open by design: a missing, failed or slow fetch NEVER blocks tracking —
 * the SDK falls back to the cached profile, then the inline profile, then the
 * built-in defaults. The remote profile carries the per-site selectors,
 * URL→category taxonomy, event toggles, consent mode and privacy settings that
 * are managed centrally in the dashboard.
 */

import type { CDPCoreConfig, SiteProfile } from './types';
import { STORAGE_KEYS } from './types';
import { getStorage, setStorage } from './storage';
import { log, warn } from './utils';

interface CachedConfig {
  profile: SiteProfile;
  fetchedAt: number;
  writeKey: string;
}

/** Shape of the profile the universal loader (monfri.global.js) pre-fetched. */
interface PreloadedConfig {
  profile?: SiteProfile;
  writeKey?: string;
  fetchedAt?: number;
}

/**
 * The universal loader fetches /v1/config BEFORE injecting the bundle and
 * leaves the result on `window.__cdpSiteConfig`. Consuming it here (a) avoids
 * a second config fetch on every cold start and (b) gives plugin activation a
 * synchronous view of the declared siteType/platform.
 */
export function readPreloadedProfile(writeKey: string): SiteProfile | null {
  try {
    const pre = (window as unknown as { __cdpSiteConfig?: PreloadedConfig }).__cdpSiteConfig;
    if (
      pre &&
      pre.profile &&
      typeof pre.profile === 'object' &&
      (!pre.writeKey || pre.writeKey === writeKey)
    ) {
      return pre.profile;
    }
  } catch {
    /* no window / exotic environment */
  }
  return null;
}

/**
 * The profile already in hand before init resolves anything — the loader's
 * preload, else this key's cached copy. Read-only. The init uses it to open the
 * persistence gate before the first write on a site that does not ask for
 * consent, so an implied site writes exactly what it always wrote (СТ22).
 */
export function peekProfile(writeKey: string): SiteProfile | null {
  const preloaded = readPreloadedProfile(writeKey);
  if (preloaded) return preloaded;
  const cached = getStorage<CachedConfig>(STORAGE_KEYS.SITE_CONFIG);
  return cached && cached.writeKey === writeKey ? cached.profile : null;
}

// Serve a cached profile without a foreground wait for up to 6h, but always
// refresh in the background (SWR). The profile changes rarely (dashboard edits),
// and `version` lets a save propagate on the next refresh.
const CONFIG_TTL_MS = 6 * 60 * 60 * 1000;
// Hard cap on the cold-start fetch so a slow network can never delay first
// tracking by more than this.
const FETCH_TIMEOUT_MS = 3000;

/**
 * Built-in defaults applied under every profile — news-leaning and privacy-first
 * so a brand-new site is compliant and lean before any dashboard tuning.
 *
 * IMPORTANT: `consent.mode` is deliberately LEFT UNSET here so it resolves at
 * init: it defaults to `implied` (full tracking → normal persistent, per-visitor
 * profiles, no fragmentation). A privacy posture (anonymous-until-consent /
 * strict / cmp-only) is per site — every site set up since 07.10 gets
 * anonymous-until-consent at provisioning, and since 20.10 the dashboard asks the
 * owner of an implied site to choose (ЛО-13). A configured Site Profile value
 * overrides it. (Pinning it here would let the default win over per-site config —
 * and previously flipped live sites into lite mode by accident.)
 * `privacy.fingerprint` is not read at all since 20.10: no mode fingerprints.
 */
export const DEFAULT_PROFILE: SiteProfile = {
  events: {
    scrollMilestones: false,
    suppressPageOnArticle: true,
    trackEngaged: true,
    trackRead: true,
    trackShares: true,
  },
  consent: { tcf: true, honorGpcDnt: true },
  privacy: { anonymizeIp: true },
};

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

/** Merge `over` onto `base`: nested objects merge one level, arrays/scalars replace. */
export function mergeProfile(
  base: SiteProfile,
  over?: Partial<SiteProfile> | null
): SiteProfile {
  if (!over) return { ...base };
  const out: Record<string, unknown> = { ...base };
  for (const [k, v] of Object.entries(over)) {
    if (v === undefined) continue;
    const cur = out[k];
    if (isObject(v) && isObject(cur)) {
      out[k] = { ...cur, ...v };
    } else {
      out[k] = v;
    }
  }
  return out as SiteProfile;
}

/**
 * This host's entry in `domainOverrides` — the exact hostname, as before 29.10 (an entry never applies to a `www.`
 * spelling it was not written for). A workspace can hold sites of different types (a media and its shop,
 * MEDIA-COMMERCE-2026-10-29 §1.1): the entry carries the site's `siteType`/`platform`.
 */
export function domainOverrideFor(
  profile: SiteProfile | null | undefined,
  host: string = typeof window !== 'undefined' ? window.location.hostname : ''
): Partial<SiteProfile> | undefined {
  const all = profile?.domainOverrides;
  if (!all || typeof all !== 'object') return undefined;
  return all[(host || '').toLowerCase()] || undefined;
}

/** Apply a per-hostname override (another site of the workspace) over the profile. */
export function applyDomainOverride(profile: SiteProfile): SiteProfile {
  try {
    const ov = domainOverrideFor(profile);
    return ov ? mergeProfile(profile, ov) : profile;
  } catch {
    return profile;
  }
}

/** Resolve the effective profile from its three layers. */
function resolve(
  remoteOrCache: SiteProfile | null,
  inline: Partial<SiteProfile> | null
): SiteProfile {
  return applyDomainOverride(
    mergeProfile(mergeProfile(DEFAULT_PROFILE, remoteOrCache), inline)
  );
}

async function fetchRemoteProfile(
  apiHost: string,
  writeKey: string
): Promise<SiteProfile | null> {
  const url = `${apiHost}/v1/config?key=${encodeURIComponent(writeKey)}`;
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    : null;
  try {
    const res = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller?.signal,
    });
    if (!res.ok) {
      warn('Site config fetch non-OK:', res.status);
      return null;
    }
    const body = (await res.json()) as SiteProfile;
    return body && typeof body === 'object' ? body : null;
  } catch (e) {
    warn('Site config fetch error:', e instanceof Error ? e.message : e);
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export interface SiteConfigHandle {
  /** Best profile available right now. Mutated in place when a refresh lands so
   *  holders that captured this reference observe the update. */
  current: SiteProfile;
  /** Resolves with the freshest profile — immediately on a warm cache or when
   *  remoteConfig is off, otherwise when the (timeout-bounded) fetch settles. */
  ready: Promise<SiteProfile>;
}

/** Swap `target`'s contents for `next` while keeping the same object reference. */
function replaceInPlace(target: SiteProfile, next: SiteProfile): void {
  for (const k of Object.keys(target)) delete (target as Record<string, unknown>)[k];
  Object.assign(target, next);
}

/**
 * Kick off Site Profile loading. Returns the best-effort profile synchronously
 * (so init never blocks) plus a `ready` promise for cold-start correctness.
 */
export function loadSiteConfig(config: CDPCoreConfig): SiteConfigHandle {
  const inline = config.siteProfile || null;

  // Loader preload wins: it is seconds old, so persist it as the cache and
  // skip the fetch entirely (the loader already paid for it).
  if (config.remoteConfig !== false) {
    const preloaded = readPreloadedProfile(config.writeKey);
    if (preloaded) {
      setStorage<CachedConfig>(STORAGE_KEYS.SITE_CONFIG, {
        profile: preloaded,
        fetchedAt: Date.now(),
        writeKey: config.writeKey,
      });
      log('Site profile preloaded by loader (v' + (preloaded.version ?? '?') + ')');
      const current = resolve(preloaded, inline);
      return { current, ready: Promise.resolve(current) };
    }
  }

  const cached = getStorage<CachedConfig>(STORAGE_KEYS.SITE_CONFIG);
  const cachedProfile =
    cached && cached.writeKey === config.writeKey ? cached.profile : null;

  const current = resolve(cachedProfile, inline);
  const handle: SiteConfigHandle = { current, ready: Promise.resolve(current) };

  if (config.remoteConfig === false) return handle;

  const fresh =
    !!cached &&
    cached.writeKey === config.writeKey &&
    Date.now() - cached.fetchedAt < CONFIG_TTL_MS;

  const refresh = fetchRemoteProfile(config.apiHost, config.writeKey).then(
    (remote) => {
      if (remote) {
        setStorage<CachedConfig>(STORAGE_KEYS.SITE_CONFIG, {
          profile: remote,
          fetchedAt: Date.now(),
          writeKey: config.writeKey,
        });
        replaceInPlace(handle.current, resolve(remote, inline));
        log('Site profile loaded (v' + (remote.version ?? '?') + ')');
      }
      return handle.current;
    }
  );

  // Warm cache → ready now; background refresh continues (SWR). Cold start →
  // ready waits for the fetch so the first article view sees the site taxonomy.
  handle.ready = fresh ? Promise.resolve(handle.current) : refresh;
  return handle;
}
